import type { Invariant, VerrouInvariant } from '@rosetta/shared';

/** A piece of a text: plain, or the occurrence of an invariant. */
export type Segment =
  | { type: 'texte'; texte: string }
  | { type: 'invariant'; texte: string; verrou: VerrouInvariant };

interface Occurrence {
  debut: number;
  fin: number;
  verrou: VerrouInvariant;
}

const LETTRE_OU_CHIFFRE = /[\p{L}\p{N}]/u;

/** A letter or a digit is on this side of the match: it would cut a longer word or number. */
function coupeUnMot(texte: string, debut: number, fin: number): boolean {
  const avant = debut > 0 ? texte.slice(debut - 1, debut) : '';
  const apres = texte.slice(fin, fin + 1);
  const premier = texte.slice(debut, debut + 1);
  const dernier = texte.slice(fin - 1, fin);
  return (
    (LETTRE_OU_CHIFFRE.test(avant) && LETTRE_OU_CHIFFRE.test(premier)) ||
    (LETTRE_OU_CHIFFRE.test(apres) && LETTRE_OU_CHIFFRE.test(dernier))
  );
}

/** Every place of `texte` where the invariant is written exactly as it is. */
function occurrencesDe(texte: string, invariant: Invariant): Occurrence[] {
  const trouvees: Occurrence[] = [];
  let depart = 0;
  while (depart <= texte.length - invariant.valeur.length) {
    const debut = texte.indexOf(invariant.valeur, depart);
    if (debut === -1) break;
    const fin = debut + invariant.valeur.length;
    if (!coupeUnMot(texte, debut, fin)) {
      trouvees.push({ debut, fin, verrou: invariant.verrou });
    }
    depart = debut + 1;
  }
  return trouvees;
}

/**
 * Cuts a text into plain pieces and pieces that are an invariant. An invariant is found
 * only where it is written exactly as Tahiti gave it, in the text of origin as in the
 * translation: a flexible one that the translation reworded is not found there, and so not
 * marked (nothing is guessed). All its occurrences are marked, except the ones that cut a
 * longer word or number ("15" in "150"). When two overlap, the longer wins (the strict one
 * on a tie); nothing is marked twice.
 */
export function decouperInvariants(texte: string, invariants: readonly Invariant[]): Segment[] {
  const candidates = invariants.flatMap((invariant) => occurrencesDe(texte, invariant));
  candidates.sort(
    (a, b) =>
      b.fin - b.debut - (a.fin - a.debut) ||
      Number(b.verrou === 'strict') - Number(a.verrou === 'strict') ||
      a.debut - b.debut,
  );

  const retenues: Occurrence[] = [];
  for (const candidate of candidates) {
    const chevauche = retenues.some((r) => candidate.debut < r.fin && r.debut < candidate.fin);
    if (!chevauche) retenues.push(candidate);
  }
  retenues.sort((a, b) => a.debut - b.debut);

  const segments: Segment[] = [];
  let curseur = 0;
  for (const { debut, fin, verrou } of retenues) {
    if (debut > curseur) segments.push({ type: 'texte', texte: texte.slice(curseur, debut) });
    segments.push({ type: 'invariant', texte: texte.slice(debut, fin), verrou });
    curseur = fin;
  }
  if (curseur < texte.length) segments.push({ type: 'texte', texte: texte.slice(curseur) });
  return segments;
}
